import { Typography } from '@mui/material';
import { getRecieverFactoringRequest } from 'lib/api/factoring';
import { getAllPayments, getCompanyPayoutGroup } from 'lib/api/payments';
import { NestedPayment, PaymentGroup, Payout } from 'lib/api/payments/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomAlert from 'lib/designSystem/Alert';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import {
  canReverseSource,
  getReversePermission,
} from 'lib/functions/reversePermissions';
import { formatDate, hasAnyPermissions, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import PayOutDetails from './components/Details';
import ExecuteGlobalPayments from './components/ExecuteGlobalPayments';
import NewPayOut from './components/MainDialog';
import ReverseConfirmation from './components/ReverseConfirmation';
import WithoutPayroll from './components/WithoutPayroll';
import { PayOutRequest } from './types';

interface HeaderConfig {
  key: string;
  label: string;
  type?: 'money' | 'date' | 'text' | 'numeric-id' | 'status-chip';
  formatter?: (value: Date, format?: string) => string;
}

const headers: HeaderConfig[] = [
  // { key: 'businessOriginName', label: 'Cuenta Origen' },
  // { key: 'bankAccountOriginName', label: 'Banco Origen' },
  { key: 'businessName', label: 'Cliente destino' },
  { key: 'businessIdentifier', label: 'Rut destino' },
  // { key: 'bankAccountDestinationName', label: 'Banco Destino' },
  { key: 'sourceTranslation', label: 'Fuente' },
  { key: 'sourceId', label: 'Id', type: 'numeric-id' },
  { key: 'amount', label: 'Monto', type: 'money' },
  {
    key: 'executedAt',
    label: 'Fecha de pago',
    type: 'date',
    formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
  },
  { key: 'statusEsp', label: 'Estado', type: 'status-chip' },
];

const payoutsRawHeaders: { key: string; label: string; type?: string }[] = [
  { key: 'bankAccountOriginName', label: 'Banco Origen' },
  // { key: 'bankAccountOriginName', label: 'Banco Origen' },
  { key: 'businessDestinationName', label: 'Cliente Destino' },
  { key: 'bankAccountDestinationName', label: 'Banco Destino' },
  { key: 'sourceTranslation', label: 'Fuente' },
  { key: 'sourceId', label: 'Id', type: 'numeric-id' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Completada', type: 'date' },
];

const Payouts: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState('');
  const [failure, setFailure] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [payments, setPayments] = useState<PaymentGroup[]>([]);
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [selectedPayouts, setSelectedPayouts] = useState<number[]>([]);
  const [source, setSource] = useState('ALL');
  const [sourceId, setSourceId] = useState(null);
  const [paymentGroupedDetails, setPaymentGroupedDetails] = useState<
    Partial<PayOutRequest> & {
      source: string;
      sourceId: number | string;
      sourceTranslation?: string;
      payoutStatusEsp?: string;
      [key: string]: unknown;
    }
  >({
    source: '',
    sourceId: '',
    sourceTranslation: '',
  });
  const [status, setStatus] = useState('PENDING');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const PAGE_SIZE = 10;
  const [view, setView] = useState<'payments' | 'payouts'>('payments');
  const [openWithoutPayrollDialog, setOpenWithoutPayrollDialog] =
    useState(false);
  const [openReverse, setOpenReverse] = useState(false);
  const [reversePayment, setReversePayment] = useState<{
    source: string;
    sourceId: number;
  } | null>(null);
  const [payoutsForGlobalPayments, setPayoutsForGlobalPayments] = useState<
    (Payout | NestedPayment)[]
  >([]);
  const [openGlobalPayments, setOpenGlobalPayments] = useState(false);

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (sourceId) params.set('sourceId', String(sourceId));
    if (businessIdentifier)
      params.set('businessIdentifier', businessIdentifier);
    if (businessName) params.set('businessName', businessName);
    if (page) params.set('page', String(page));
    if (pageSize) params.set('pageSize', String(pageSize));
    if (status) params.set('status', status);
    return params.toString();
  };

  const fetchPaymentsOut = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllPayments(dispatch, query);
      if (!response)
        return showAlert(dispatch, 'error', 'Error al cargar los pagos');
      setPayments(response.payments);
      setPayouts(response.payouts);
      setLoading(false);
      return response;
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al cargar los pagos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchPaymentsOut({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleOpenSolicitudes = () => {
    setOpenWithoutPayrollDialog(true);
  };
  const handleOpenGlobalPayments = () => {
    if (view === 'payments' && selectedPayments.length > 0) {
      const paymentsSelected = payments.filter((payment) =>
        selectedPayments.includes(payment.id),
      );
      const payoutsSelected = paymentsSelected.flatMap(
        (payment) => payment.payments,
      );
      setPayoutsForGlobalPayments(payoutsSelected);
      setOpenGlobalPayments(true);
    } else if (view === 'payouts' && selectedPayouts.length > 0) {
      const payoutsSelected = payouts.filter((payout) =>
        selectedPayouts.includes(payout.id),
      );
      setPayoutsForGlobalPayments(payoutsSelected);
      setOpenGlobalPayments(true);
    }
  };

  const handleCloseGlobalPayments = () => {
    setPayoutsForGlobalPayments([]);
    setOpenGlobalPayments(false);
  };

  const filters: Filter[] = [
    {
      label: 'Rut destino',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre destino',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Fuente',
      type: 'select',
      value: source,
      setter: setSource,
      options: [
        { label: 'Todas', value: 'ALL' },
        { label: 'Factoring', value: 'factoring-request' },
        { label: 'Confirming', value: 'confirming-request' },
        { label: 'Crédito', value: 'credit-request' },
        { label: 'Pago proveedores', value: 'payment-request' },
        { label: 'Dev. pasivos', value: 'payment-request-passive' },
      ],
    },
    {
      label: 'Id',
      type: 'text',
      value: sourceId,
      setter: setSourceId,
    },
    {
      label: 'Estado',
      type: 'select',
      value: status,
      setter: setStatus,
      options: [
        { label: 'Todas', value: 'ALL' },
        { label: 'Ejecutada', value: 'EXECUTED' },
        { label: 'Pendiente', value: 'PENDING' },
      ],
    },
  ];

  const triggerSearch = async () => {
    setLoading(true);
    await fetchPaymentsOut({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Sin nómina',
      action: handleOpenSolicitudes,
      color: 'warning',
      disabled: !hasAnyPermissions(user, ['financing_payout_view']),
    },
    {
      name: 'Buscar',
      action: () => {},
      color: 'secondary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
    {
      name: 'Pago a proveedores',
      action: handleOpen,
      color: 'primary',
      disabled: !hasAnyPermissions(user, ['financing_payout_new']),
    },
    {
      name: 'Completar pagos',
      action: handleOpenGlobalPayments,
      color: 'primary',
      disabled:
        !hasAnyPermissions(user, ['financing_payout_execute']) ||
        (selectedPayments.length === 0 && view === 'payments') ||
        (selectedPayouts.length === 0 && view === 'payouts'),
    },
  ];

  const refreshPaymentGroupDetails = async (
    source: string,
    sourceId: string,
  ) => {
    try {
      let paymentGroupedDetailResponse = await getCompanyPayoutGroup(
        dispatch,
        source,
        parseInt(sourceId),
      );
      if (source === 'factoring-request') {
        const requestRsponse = await getRecieverFactoringRequest(dispatch, {
          requestId: sourceId,
        });
        paymentGroupedDetailResponse = {
          ...paymentGroupedDetailResponse,
          ...requestRsponse,
        };
      }
      setPaymentGroupedDetails(paymentGroupedDetailResponse);
    } catch (error) {
      console.error('error', error);
      alert('Error al actualizar los detalles del pago');
    }
  };

  const handleDetails = async (id: string) => {
    const [source, sourceId] = id.split('/');
    const payment: PaymentGroup | undefined = payments.find(
      (payment: { source: string; sourceId: string }) =>
        payment.source === source && payment.sourceId === sourceId,
    );
    if (payment) {
      try {
        setLoading(true);
        await refreshPaymentGroupDetails(
          payment.source,
          payment.sourceId.toString(),
        );
        setOpenDetails(true);
      } catch (error) {
        console.error('error', error);
        showAlert(dispatch, 'error', 'Error al cargar los pagos');
      }
      setLoading(false);
    }
  };

  const handleReverse = (id: string) => {
    const [source, sourceId] = id.split('/');
    setReversePayment({ source, sourceId: parseInt(sourceId) });
    setOpenReverse(true);
  };

  const canShowReverse = (id: string) => {
    if (!id) return false;

    const payment = payments.find((p) => p.id === id);
    if (!payment) return false;

    if (!payment.executedAt) return false;

    const [source] = id.split('/');
    if (!canReverseSource(source)) return false;

    const permission = getReversePermission(source);
    if (!permission) return false;
    return hasAnyPermissions(user, [permission]);
  };

  const cantSelectRow = (row: PaymentGroup | Payout) => {
    return row.status != 'PENDING';
  };

  const canSelectAll = () => {
    return view === 'payments'
      ? payments.every((payment) => payment.status === 'PENDING')
      : payouts.every((payout) => payout.status === 'PENDING');
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Detalles',
    },
    {
      action: handleReverse,
      actionLabel: 'Reversar',
      canShow: canShowReverse,
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        customTitle={
          <Typography variant="body1">
            {view === 'payments'
              ? 'Acá puedes ver tus nóminas. '
              : 'Acá puedes ver los pagos.'}
            <span
              style={{ color: 'blue', cursor: 'pointer' }}
              onClick={() => {
                setView(view === 'payments' ? 'payouts' : 'payments');
                setSelectedPayments([]);
                setSelectedPayouts([]);
              }}
            >
              {view === 'payments'
                ? ' Ver todos las transferencias'
                : ' Ver pagos agrupados'}
            </span>
          </Typography>
        }
        buttons={buttons}
        variant="body1"
      />
      {view === 'payments' ? (
        <EnhancedTable
          exportToFile="xlsx"
          dataHeaders={headers}
          data={payments}
          rowsPerPageDefault={50}
          actions={actions}
          showActions
          lastElement
          canSelectAll={canSelectAll()}
          cantSelectRow={cantSelectRow}
          selected={selectedPayments}
          setSelected={setSelectedPayments}
        />
      ) : (
        <EnhancedTable
          exportToFile="xlsx"
          dataHeaders={payoutsRawHeaders}
          data={payouts}
          rowsPerPageDefault={50}
          lastElement
          canSelectAll={canSelectAll()}
          cantSelectRow={cantSelectRow}
          selected={selectedPayouts}
          setSelected={setSelectedPayouts}
        />
      )}
      {open && (
        <NewPayOut
          setOpen={setOpen}
          open={open}
          setSuccessOpen={setSuccess}
          setFailureOpen={setFailure}
          setUp={triggerSearch}
        />
      )}
      {openDetails && (
        <PayOutDetails
          open={openDetails}
          setOpen={setOpenDetails}
          paymentGroupedDetails={paymentGroupedDetails}
          setSuccess={setSuccess}
          setFailure={setFailure}
          setUp={triggerSearch}
          refreshPaymentGroupDetails={refreshPaymentGroupDetails}
        />
      )}
      {openWithoutPayrollDialog && (
        <WithoutPayroll
          open={openWithoutPayrollDialog}
          setOpen={setOpenWithoutPayrollDialog}
        />
      )}
      {openReverse && reversePayment && (
        <ReverseConfirmation
          open={openReverse}
          setOpen={setOpenReverse}
          source={reversePayment.source}
          sourceId={reversePayment.sourceId}
          setUp={triggerSearch}
        />
      )}
      {openGlobalPayments && payoutsForGlobalPayments.length > 0 && (
        <ExecuteGlobalPayments
          open={openGlobalPayments}
          onClose={handleCloseGlobalPayments}
          payouts={payoutsForGlobalPayments}
          setUp={triggerSearch}
        />
      )}
      <CustomAlert
        open={success !== ''}
        title="Éxito"
        onClose={() => {
          setSuccess('');
        }}
        text={success}
        severity="success"
        size="small"
        variant="outlined"
      />
      <CustomAlert
        open={failure !== ''}
        title="Error"
        onClose={() => {
          setFailure('');
        }}
        text={failure}
        severity="error"
        size="small"
        variant="outlined"
      />
    </div>
  );
};

export default Payouts;
